import { c as _c } from "react/compiler-runtime";
import React, { ReactElement, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import CarouselDotsComponent from './carousel-dots.component';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import debounce from 'lodash.debounce';
import styles from './carousel.component.module.scss';
import useIsCSR from '@spa/lib/react/hooks/useIsCSR';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';

/** The browser clamps scrollLeft to the scrollable range, so sub-pixel gaps never trigger a scroll event. */
const SCROLL_POSITION_TOLERANCE: number = 1;
export enum CarouselType {
  BANNER,
  CONTENT,
  HEADER,
  MEDIA,
  MOBILE_ICON,
  PRODUCT,
  NAVIGATION,
}
export interface Props {
  readonly id: string;
  readonly children: ReadonlyArray<ReactElement>;
  readonly carouselType: CarouselType;
  readonly navigation?: boolean;
  readonly autoPlay?: boolean;
  readonly autoPlayDelay?: number;
  readonly title?: string;
  readonly startIndex?: number;
  readonly onCurrentItemChange?: (itemIdx: number) => void;
  readonly onCurrentItemSettled?: (itemIdx: number) => void;
  readonly snapToEveryItem?: boolean;
}
function CarouselComponent(t0) {
  const $ = _c(126);
  const {
    id,
    children,
    navigation: t1,
    carouselType,
    autoPlay: t2,
    autoPlayDelay: t3,
    title,
    startIndex,
    onCurrentItemChange,
    onCurrentItemSettled,
    snapToEveryItem: t4
  } = t0;
  const navigation = t1 === undefined ? false : t1;
  const autoPlay = t2 === undefined ? false : t2;
  const autoPlayDelay = t3 === undefined ? 5000 : t3;
  const snapToEveryItem = t4 === undefined ? false : t4;
  const {
    formatMessage
  } = useNamespacedIntl("carousel");
  const swiperRef = useRef(null);
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const tilesRef = useRef(t5);
  const stopScrollingTimeoutRef = useRef(null);
  const reportedItemRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const t6 = startIndex ?? 0;
  let t7;
  if ($[1] !== startIndex) {
    t7 = {
      startIndex
    };
    $[1] = startIndex;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  let t9;
  if ($[4] !== t6 || $[5] !== t7) {
    t9 = {
      currentItem: t6,
      isScrolling: false,
      propSnapshot: t7,
      swiperWidth: 0,
      tileWidths: t8 as Array<number>
    };
    $[4] = t6;
    $[5] = t7;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const [state, updateState] = useObjectReducer(t9);
  const isCsr = useIsCSR();
  const {
    currentItem,
    isScrolling,
    propSnapshot,
    swiperWidth,
    tileWidths
  } = state;
  if (startIndex !== propSnapshot.startIndex) {
    updateState({
      currentItem: startIndex ?? 0,
      propSnapshot: {
        startIndex
      }
    });
  }
  let t10;
  let t11;
  if ($[7] !== children.length || $[8] !== updateState) {
    t10 = () => {
      const updateMeasurements = () => {
        const container = swiperRef.current;
        if (container) {
          const tiles = tilesRef.current.slice(0, children.length);
          updateState({
            swiperWidth: container.offsetWidth,
            tileWidths: tiles.map(_temp)
          });
        }
      };
      const debouncedUpdate = debounce(updateMeasurements, 100);
      updateMeasurements();
      const ro = new ResizeObserver(debouncedUpdate);
      if (swiperRef.current) {
        ro.observe(swiperRef.current);
      }
      return () => {
        ro.disconnect();
        debouncedUpdate.cancel();
      };
    };
    t11 = [children.length, updateState];
    $[7] = children.length;
    $[8] = updateState;
    $[9] = t10;
    $[10] = t11;
  } else {
    t10 = $[9];
    t11 = $[10];
  }
  useEffect(t10, t11);
  let result = 0;
  if (isCsr && swiperWidth > 0 && tileWidths.length > 0) {
    let t12;
    if ($[11] !== swiperWidth || $[12] !== tileWidths) {
      let lastPageWidth = 0;
      t12 = [...tileWidths].reverse().filter(width => {
        lastPageWidth = lastPageWidth + width;
        lastPageWidth;
        return lastPageWidth <= swiperWidth;
      });
      $[11] = swiperWidth;
      $[12] = tileWidths;
      $[13] = t12;
    } else {
      t12 = $[13];
    }
    const countOnLast = t12.length;
    const rawMax = tileWidths.length - countOnLast;
    result = Math.max(0, Math.min(rawMax, tileWidths.length - 1));
  }
  const maxNavigation = result;
  let t12;
  if ($[14] !== children.length) {
    t12 = () => tilesRef.current.slice(0, children.length).map(_temp2);
    $[14] = children.length;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const getTileOffsets = t12;
  let t13;
  if ($[16] !== getTileOffsets) {
    t13 = scrollPosition => {
      const offsets = getTileOffsets();
      const itemIdx = offsets.findIndex(offset => scrollPosition <= offset);
      return itemIdx === -1 ? Math.max(offsets.length - 1, 0) : itemIdx;
    };
    $[16] = getTileOffsets;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const getItemAtScrollPosition = t13;
  let t14;
  if ($[18] !== children.length || $[19] !== currentItem || $[20] !== getItemAtScrollPosition || $[21] !== updateState) {
    t14 = () => {
      const currentSwiper = swiperRef.current;
      if (currentSwiper && children.length > 0) {
        const itemIdx_0 = getItemAtScrollPosition(Math.trunc(currentSwiper.scrollLeft));
        if (currentItem !== itemIdx_0) {
          updateState({
            currentItem: itemIdx_0
          });
        }
      }
    };
    $[18] = children.length;
    $[19] = currentItem;
    $[20] = getItemAtScrollPosition;
    $[21] = updateState;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const calculateCurrentTile = t14;
  let t15;
  if ($[23] !== getTileOffsets) {
    t15 = () => {
      const scrollLeft = swiperRef.current?.scrollLeft ?? 0;
      const distances = getTileOffsets().map(offset_0 => Math.abs(offset_0 - scrollLeft));
      return Math.max(distances.indexOf(Math.min(...distances)), 0);
    };
    $[23] = getTileOffsets;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const getNearestItem = t15;
  let t16;
  if ($[25] !== onCurrentItemChange) {
    t16 = itemIdx_1 => {
      if (onCurrentItemChange && reportedItemRef.current !== itemIdx_1) {
        reportedItemRef.current = itemIdx_1;
        onCurrentItemChange(itemIdx_1);
      }
    };
    $[25] = onCurrentItemChange;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const reportCurrentItem = t16;
  let t17;
  if ($[27] !== carouselType || $[28] !== getTileOffsets) {
    t17 = (itemIdx_2, t18) => {
      const centerTile = t18 === undefined ? false : t18;
      const tileOffset = getTileOffsets()[itemIdx_2];
      const swiper = swiperRef.current;
      if (!swiper) {
        return tileOffset;
      }
      const tile_1 = tilesRef.current[itemIdx_2];
      const shouldCenterTile = centerTile || carouselType === CarouselType.NAVIGATION;
      const targetPosition = shouldCenterTile && tile_1 ? tileOffset - (swiper.clientWidth - tile_1.clientWidth) / 2 : tileOffset;
      swiper.scrollTo({
        left: targetPosition,
        behavior: "smooth"
      });
      const maxScrollPosition = Math.max(swiper.scrollWidth - swiper.clientWidth, 0);
      const clampedPosition = Math.min(Math.max(targetPosition, 0), maxScrollPosition);
      const willScroll = Math.abs(clampedPosition - swiper.scrollLeft) > SCROLL_POSITION_TOLERANCE;
      if (willScroll) {
        isProgrammaticScrollRef.current = true;
      }
      return clampedPosition;
    };
    $[27] = carouselType;
    $[28] = getTileOffsets;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  const scrollToPosition = t17;
  let t18;
  if ($[30] !== reportCurrentItem || $[31] !== scrollToPosition || $[32] !== updateState) {
    t18 = itemIdx_3 => {
      scrollToPosition(itemIdx_3);
      updateState({
        currentItem: itemIdx_3
      });
      reportCurrentItem(itemIdx_3);
    };
    $[30] = reportCurrentItem;
    $[31] = scrollToPosition;
    $[32] = updateState;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const scrollToItem = t18;
  let t19;
  let t20;
  if ($[34] !== autoPlay || $[35] !== autoPlayDelay || $[36] !== currentItem || $[37] !== isScrolling || $[38] !== maxNavigation || $[39] !== scrollToItem) {
    t19 = () => {
      let timer;
      const handleInfiniteNext = () => {
        let newCurrentItem = currentItem + 1;
        if (newCurrentItem > maxNavigation) {
          newCurrentItem = 0;
        }
        scrollToItem(newCurrentItem);
      };
      if (autoPlay && !isScrolling) {
        timer = setTimeout(() => handleInfiniteNext(), autoPlayDelay);
      }
      return () => clearTimeout(timer);
    };
    t20 = [autoPlay, autoPlayDelay, currentItem, isScrolling, maxNavigation, scrollToItem];
    $[34] = autoPlay;
    $[35] = autoPlayDelay;
    $[36] = currentItem;
    $[37] = isScrolling;
    $[38] = maxNavigation;
    $[39] = scrollToItem;
    $[40] = t19;
    $[41] = t20;
  } else {
    t19 = $[40];
    t20 = $[41];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[42] !== carouselType || $[43] !== getItemAtScrollPosition || $[44] !== isCsr || $[45] !== onCurrentItemChange || $[46] !== scrollToPosition || $[47] !== startIndex || $[48] !== tileWidths.length || $[49] !== updateState) {
    t21 = () => {
      const areTilesMeasured = isCsr && tileWidths.length > 0;
      const isAlreadyReported = onCurrentItemChange !== undefined && startIndex === reportedItemRef.current;
      if (startIndex === undefined || !areTilesMeasured || isAlreadyReported) {
        return;
      }
      reportedItemRef.current = startIndex;
      const centerTile_0 = carouselType === CarouselType.MEDIA;
      const scrollPosition_0 = scrollToPosition(startIndex, centerTile_0);
      if (centerTile_0) {
        updateState({
          currentItem: getItemAtScrollPosition(scrollPosition_0)
        });
      }
    };
    t22 = [carouselType, getItemAtScrollPosition, isCsr, onCurrentItemChange, startIndex, scrollToPosition, tileWidths.length, updateState];
    $[42] = carouselType;
    $[43] = getItemAtScrollPosition;
    $[44] = isCsr;
    $[45] = onCurrentItemChange;
    $[46] = scrollToPosition;
    $[47] = startIndex;
    $[48] = tileWidths.length;
    $[49] = updateState;
    $[50] = t21;
    $[51] = t22;
  } else {
    t21 = $[50];
    t22 = $[51];
  }
  useLayoutEffect(t21, t22);
  let t23;
  if ($[52] !== carouselType || $[53] !== currentItem) {
    t23 = classNames([styles["carousel__prev-button"]], {
      [styles["carousel__prev-button--content"]]: carouselType === CarouselType.CONTENT,
      [styles["carousel__prev-button--product"]]: carouselType === CarouselType.PRODUCT,
      [styles["carousel__prev-button--header"]]: carouselType === CarouselType.HEADER,
      [styles["carousel__prev-button--media"]]: carouselType === CarouselType.MEDIA,
      [styles["carousel__prev-button--banner"]]: carouselType === CarouselType.BANNER,
      [styles["carousel__prev-button--mobile-icon"]]: carouselType === CarouselType.MOBILE_ICON,
      [styles["carousel__prev-button--hidden"]]: currentItem === 0
    });
    $[52] = carouselType;
    $[53] = currentItem;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  const prevButtonClassNames = t23;
  let t24;
  if ($[55] !== carouselType || $[56] !== currentItem || $[57] !== isCsr || $[58] !== maxNavigation) {
    t24 = classNames([styles["carousel__next-button"]], {
      [styles["carousel__next-button--content"]]: carouselType === CarouselType.CONTENT,
      [styles["carousel__next-button--product"]]: carouselType === CarouselType.PRODUCT,
      [styles["carousel__next-button--header"]]: carouselType === CarouselType.HEADER,
      [styles["carousel__next-button--media"]]: carouselType === CarouselType.MEDIA,
      [styles["carousel__next-button--banner"]]: carouselType === CarouselType.BANNER,
      [styles["carousel__next-button--mobile-icon"]]: carouselType === CarouselType.MOBILE_ICON,
      [styles["carousel__next-button--loading"]]: !isCsr,
      [styles["carousel__next-button--hidden"]]: currentItem === maxNavigation
    });
    $[55] = carouselType;
    $[56] = currentItem;
    $[57] = isCsr;
    $[58] = maxNavigation;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  const nextButtonClassNames = t24;
  let t25;
  if ($[60] !== carouselType || $[61] !== snapToEveryItem) {
    t25 = classNames([styles.carousel__item], {
      [styles["carousel__item--banner"]]: carouselType === CarouselType.BANNER,
      [styles["carousel__item--navigation"]]: carouselType === CarouselType.NAVIGATION,
      [styles["carousel__item--snap-stop"]]: snapToEveryItem
    });
    $[60] = carouselType;
    $[61] = snapToEveryItem;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  const itemClassNames = t25;
  let t26;
  if ($[63] !== carouselType) {
    t26 = classNames([styles["carousel__navigation-wrapper"]], {
      [styles["carousel__navigation-wrapper--banner"]]: carouselType === CarouselType.BANNER
    });
    $[63] = carouselType;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  const navigationClassNames = t26;
  let t27;
  if ($[65] !== currentItem || $[66] !== scrollToItem) {
    t27 = () => {
      const newCurrentItem_0 = currentItem + 1;
      scrollToItem(newCurrentItem_0);
    };
    $[65] = currentItem;
    $[66] = scrollToItem;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  const handleNext = t27;
  let t28;
  if ($[68] !== currentItem || $[69] !== scrollToItem) {
    t28 = () => {
      const newCurrentItem_1 = currentItem - 1;
      scrollToItem(newCurrentItem_1);
    };
    $[68] = currentItem;
    $[69] = scrollToItem;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  const handlePrev = t28;
  let t29;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = callback => {
      if (stopScrollingTimeoutRef.current) {
        clearTimeout(stopScrollingTimeoutRef.current);
      }
      stopScrollingTimeoutRef.current = setTimeout(() => {
        callback();
      }, 50);
    };
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  const onScrollStop = t29;
  let t30;
  if ($[72] !== getNearestItem || $[73] !== onCurrentItemChange || $[74] !== reportCurrentItem) {
    t30 = () => {
      if (onCurrentItemChange) {
        reportCurrentItem(getNearestItem());
      }
    };
    $[72] = getNearestItem;
    $[73] = onCurrentItemChange;
    $[74] = reportCurrentItem;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  const reportNearestItem = t30;
  const t31 = `carousel-component-${id}`;
  let t32;
  if ($[76] !== calculateCurrentTile || $[77] !== getNearestItem || $[78] !== isScrolling || $[79] !== onCurrentItemSettled || $[80] !== reportNearestItem || $[81] !== updateState) {
    t32 = () => {
      if (!isScrolling) {
        updateState({
          isScrolling: true
        });
      }
      if (!isProgrammaticScrollRef.current) {
        reportNearestItem();
      }
      onScrollStop(() => {
        isProgrammaticScrollRef.current = false;
        onCurrentItemSettled?.(getNearestItem());
        updateState({
          isScrolling: false
        });
        calculateCurrentTile();
        reportNearestItem();
      });
    };
    $[76] = calculateCurrentTile;
    $[77] = getNearestItem;
    $[78] = isScrolling;
    $[79] = onCurrentItemSettled;
    $[80] = reportNearestItem;
    $[81] = updateState;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== carouselType) {
    t33 = classNames([styles.carousel__wrapper], {
      [styles["carousel__wrapper--navigation"]]: carouselType === CarouselType.NAVIGATION
    });
    $[83] = carouselType;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== children || $[86] !== itemClassNames) {
    t34 = children.map((child, childIdx) => <div ref={ref => {
      tilesRef.current[childIdx] = ref;
    }} key={child.key} className={itemClassNames}>{child}</div>);
    $[85] = children;
    $[86] = itemClassNames;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== t33 || $[89] !== t34) {
    t35 = <div className={t33}>{t34}</div>;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  const t36 = title ? `${title} ` + formatMessage("prevButton") : formatMessage("prevButton");
  const t37 = formatMessage("prevButton");
  const t38 = formatMessage("prevButton");
  let t39;
  if ($[91] !== t38) {
    t39 = <ImageComponent className={styles["carousel__prev-button-icon"]} src="/assets/icons/arrow_forward.svg" alt={t38} width={20} height={20} />;
    $[91] = t38;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== handlePrev || $[94] !== id || $[95] !== prevButtonClassNames || $[96] !== t36 || $[97] !== t37 || $[98] !== t39) {
    t40 = <button className={prevButtonClassNames} onClick={handlePrev} title={t36} aria-label={t37} aria-controls={id}>{t39}</button>;
    $[93] = handlePrev;
    $[94] = id;
    $[95] = prevButtonClassNames;
    $[96] = t36;
    $[97] = t37;
    $[98] = t39;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  const t41 = title ? `${title} ` + formatMessage("nextButton") : formatMessage("nextButton");
  const t42 = formatMessage("nextButton");
  const t43 = formatMessage("nextButton");
  let t44;
  if ($[100] !== t43) {
    t44 = <ImageComponent className={styles["carousel__next-button-icon"]} src="/assets/icons/arrow_forward.svg" alt={t43} width={20} height={20} />;
    $[100] = t43;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== handleNext || $[103] !== id || $[104] !== nextButtonClassNames || $[105] !== t41 || $[106] !== t42 || $[107] !== t44) {
    t45 = <button className={nextButtonClassNames} onClick={handleNext} title={t41} aria-label={t42} aria-controls={id}>{t44}</button>;
    $[102] = handleNext;
    $[103] = id;
    $[104] = nextButtonClassNames;
    $[105] = t41;
    $[106] = t42;
    $[107] = t44;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  let t46;
  if ($[109] !== t32 || $[110] !== t35 || $[111] !== t40 || $[112] !== t45) {
    t46 = <div className={styles.carousel__swiper} ref={swiperRef} onScroll={t32}>{t35}{t40}{t45}</div>;
    $[109] = t32;
    $[110] = t35;
    $[111] = t40;
    $[112] = t45;
    $[113] = t46;
  } else {
    t46 = $[113];
  }
  let t47;
  if ($[114] !== currentItem || $[115] !== isCsr || $[116] !== maxNavigation || $[117] !== navigation || $[118] !== navigationClassNames || $[119] !== scrollToItem) {
    t47 = navigation && <div className={navigationClassNames}><CarouselDotsComponent currentItem={currentItem} isCsr={isCsr} maxNavigation={maxNavigation} onClick={navIndex => {
        scrollToItem(navIndex);
      }} /></div>;
    $[114] = currentItem;
    $[115] = isCsr;
    $[116] = maxNavigation;
    $[117] = navigation;
    $[118] = navigationClassNames;
    $[119] = scrollToItem;
    $[120] = t47;
  } else {
    t47 = $[120];
  }
  let t48;
  if ($[121] !== id || $[122] !== t31 || $[123] !== t46 || $[124] !== t47) {
    t48 = <section className={styles.carousel} id={id} aria-roledescription="carousel" data-testid={t31}>{t46}{t47}</section>;
    $[121] = id;
    $[122] = t31;
    $[123] = t46;
    $[124] = t47;
    $[125] = t48;
  } else {
    t48 = $[125];
  }
  return t48;
}
function _temp2(tile_0) {
  return tile_0?.offsetLeft ?? 0;
}
function _temp(tile) {
  return tile?.offsetWidth ?? 0;
}
export default memo(CarouselComponent);